import { FailReasons } from '../constants';
import { biDefaults, WidgetName } from '../bi/consts';
import { BusinessInfo } from '@wix/bookings-uou-types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { navigateToBookingsFormPage } from '@wix/bookings-booking-navigation';
import { navigateToBookingsCalendarPage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import {
  bookingsUoUFlowBookInitialIntentSrc16Evid33,
  bookingsUoUFlowSlotSelectionCompletedSrc16Evid34,
} from '@wix/bi-logger-bookings-data/v2';

import {
  isDemoMode,
  isPricingPlanInstalled as isPricingPlanInstalledUtils,
  isTemplateMode,
  trackAnalytics,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  isCourseService,
  isFeatureEnabled,
  isServiceConnectedToPricingPlan,
  isServiceHasPricingPlanError,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { getTrackingInfoForBookButtonClick } from '@wix/bookings-calendar-catalog-viewer-analytics-adapter';
import type { Basket } from '@wix/bookings-services-preferences-modal/types';
import { Features } from '@wix/ambassador-bookings-v2-bookings-settings/types';

export type NavigateToCalendarAction = (args?: {
  initiatedBy?: string;
  basket?: Basket;
}) => Promise<void>;

export const handleNavigation = async ({
  service,
  businessInfo,
  activeFeatures,
  onNavigationFailed,
  locationId,
  timezone,
  flowAPI,
  basket,
}: {
  service: Service;
  businessInfo?: BusinessInfo;
  activeFeatures?: Features;
  onNavigationFailed: ({ failReasons }: { failReasons: FailReasons[] }) => void;
  locationId?: string;
  timezone?: string;
  flowAPI: ControllerFlowAPI;
  basket?: Basket;
}) => {
  const { isPreview } = flowAPI.environment;
  const featureEnabled = activeFeatures
    ? isFeatureEnabled({ activeFeatures, service })
    : true;
  const isPricingPlanInstalled = await isPricingPlanInstalledUtils(
    flowAPI.controllerConfig.wixCodeApi,
  );
  const { bi } = flowAPI;
  const serviceForBi: Service =
    basket?.selectedServicesPreferences?.[0]?.service || service;

  bi?.report(
    bookingsUoUFlowBookInitialIntentSrc16Evid33({
      widget_name: WidgetName.PAGE,
      bookingsServiceId: serviceForBi.id!,
      serviceType: serviceForBi.type!,
      isEligibleForFlowCompletion: featureEnabled,
    }),
  );

  if (
    isPreview ||
    isDemoMode({
      wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
      appParams: flowAPI.controllerConfig.appParams,
    }) ||
    isTemplateMode({
      wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
      appParams: flowAPI.controllerConfig.appParams,
    }) ||
    (featureEnabled &&
      (await isPricingPlanNavigateEnabled(isPricingPlanInstalled, service)))
  ) {
    const referral = biDefaults.service_page_referral_info;
    const serviceSlug = service!.mainSlug?.name!;
    const isCourse = isCourseService(service);

    if (service) {
      const trackingInfo = getTrackingInfoForBookButtonClick({
        service,
        businessName: businessInfo?.name || '',
      });

      trackAnalytics({
        wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
        eventId: trackingInfo.eventId as any,
        payload: trackingInfo.payload,
      });
    }

    const serviceId = service.id!;
    if (isCourse) {
      bi?.report(
        bookingsUoUFlowSlotSelectionCompletedSrc16Evid34({
          widget_name: WidgetName.PAGE,
          serviceType: service.type!,
          bookingsServiceId: service.id!,
          startTime: service.schedule?.firstSessionStart?.getTime(),
          endTime: service.schedule?.lastSessionEnd?.getTime(),
          isEligibleForFlowCompletion: featureEnabled,
        }),
      );

      navigateToBookingsFormPage({
        platformAPIs: flowAPI.controllerConfig.platformAPIs,
        wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
        serviceId,
        serviceSlug,
        queryParams: { referral, timezone },
      });
    } else {
      navigateToBookingsCalendarPage(
        flowAPI.controllerConfig.wixCodeApi,
        flowAPI.controllerConfig.platformAPIs,
        {
          serviceSlugOrBasket: basket || serviceSlug,
          navigationContext: {
            referral,
            location: locationId,
            timezone,
          },
        },
      );
    }
  } else {
    if (!featureEnabled) {
      onNavigationFailed({ failReasons: [FailReasons.Premium] });
    } else {
      const failReasons: FailReasons[] = [];
      if (!isPricingPlanInstalled) {
        failReasons.push(FailReasons.PricingPlanNotInstalled);
      }
      if (!isServiceConnectedToPricingPlan({ payment: service.payment! })) {
        failReasons.push(FailReasons.NoPlansAssignedToService);
      }
      onNavigationFailed({ failReasons });
    }
  }
};

const isPricingPlanNavigateEnabled = async (
  isPricingPlanInstalled: boolean,
  service: Service,
) => {
  return !isServiceHasPricingPlanError({
    payment: service.payment!,
    isPricingPlanInstalled,
  });
};
